/**
 * The purse (docs/roadmap-2026-10.md, flag `purseUI`): where a peon's gold went, one line per
 * gold_ledger row on the Rewards screen. Pure and dependency-light so the server, both clients and
 * the Node tests share one rule for the words.
 *
 * Each kind's `detail` JSON, as server/db.ts writes it:
 * - bounty: the BountyBreakdown, plus `flash: true` when a gold rush paid it. task_id set. A bounty a
 *   credit moved (docs/credit.md) is the holder's breakdown plus `credited_from`, the name it came from.
 * - reversal: { reason: "reopen" | "delete", bounty_at }, or { reason: "credit", bounty_at, to } when
 *   the credit went to `to` (docs/credit.md), or { reason: "share", bounty_at, pot, n } when a shared
 *   job's gold was split again (docs/shares.md). task_id set. A shared job's bounties carry `pot` and
 *   `shared_with` (the finisher's) or `share: true` and `with` (a helper's, or a part moved by a share).
 * - redemption: { reward_id, title }.
 * - reward_income: { reward_id, title, buyer }.
 * - luxury: { tier, title }. task_id set.
 * - refund: { reason: "delete" | "insert_failed" } with a task_id (a luxury task's gold back), or
 *   { reason: "expired", task_id, title } when an untaken luxury task ran out (server/db.ts
 *   expireLuxuryTask; the title names the line, the task being gone), or
 *   { reason: "upgrade_deleted", upgrade_id, title } from the gold upgrades (no longer written).
 * - upgrade: { upgrade_id, title } (no longer written: upgrades cost wood since 0025).
 * - wood: { wood }.
 * - cosmetic: { item, title }.
 * - wager: { wager_id, stake } for a stake (negative), { wager_id, pot } for a pot won (positive).
 *   An expired wager's stakes come back as refund { reason: "wager_expired", wager_id, title }.
 * - roulette: { task_id, done_by, stake }: task roulette's charge to the picked peon when someone
 *   else did the task (negative, never past zero; docs/roulette.md).
 * - scroll: { entry_id, week, title }: a line written on the weekly scroll (positive, title "Wrote
 *   on the scroll"; docs/weekly.md "Gold for a line"). Lines are no longer taken off (owner,
 *   2026-10-05), so nothing new is negative; a { reason: "removed" } row from before still reads by
 *   its own title.
 * - heart: { note_id, from, title }: someone (`from`) hearted the peon's pinned note (positive, title
 *   "A heart on your note"; docs/notes.md "Hearts"). Once per note and hearter, at most 50 a day.
 */
import { cosmetic } from "./cosmetics.ts";
import { creditFromNote, creditGivenNote } from "./credit.ts";
import { sharedNote } from "./shares.ts";
import { trinket } from "./trinkets.ts";
import type { PurseRow } from "./types";

/** Rows per page of the purse; "Show more" asks for the next this many. */
export const PURSE_PAGE = 30;

/** What a row needs to be named: the ledger row less its id and amount. */
export type PurseLabelInput = Pick<PurseRow, "kind" | "detail" | "task_title" | "reward_title" | "upgrade_title"> & { created_at?: string };

/** Said for a row whose task has since been deleted and carried no title of its own. */
const GONE_TASK = "A deleted task";

function text(v: unknown): string | null {
  return typeof v === "string" && v.trim() ? v.trim() : null;
}

/** A detail's list of names (a string or strings), as the purse's notes read it. */
function names(v: unknown): string[] {
  if (typeof v === "string") return v.trim() ? [v.trim()] : [];
  return Array.isArray(v) ? v.filter((n): n is string => typeof n === "string" && n.trim() !== "") : [];
}

/** The plain label for one purse line: what the gold was for. */
export function purseLabel(row: PurseLabelInput): string {
  const d = row.detail ?? {};
  switch (row.kind) {
    case "bounty":
    case "reversal":
      return text(row.task_title) ?? GONE_TASK;
    case "redemption":
    case "reward_income":
      return text(d.title) ?? text(row.reward_title) ?? "A reward";
    case "luxury":
      return text(d.title) ?? text(row.task_title) ?? GONE_TASK;
    case "refund":
      return text(d.title) ?? text(row.task_title) ?? text(row.upgrade_title) ?? "Refund";
    case "upgrade":
      return text(d.title) ?? text(row.upgrade_title) ?? "An upgrade";
    case "wood":
      return "Bought wood";
    case "wager":
      return "Wager";
    case "roulette":
      return "The wheel";
    case "scroll":
      return text(d.title) ?? "The scroll";
    case "heart":
      return text(d.title) ?? "A heart on your note";
    case "bonus":
      // A badge's one-off gold (shared/achievements.ts), or a trinket's (shared/trinkets.ts): its name.
      // A trinket reads the catalogue's name first, so a row from before a rename matches the collection.
      if (typeof d.trinket === "string") return trinket(d.trinket)?.name ?? text(d.title) ?? "A trinket";
      return text(d.title) ?? "A badge";
    case "cosmetic": {
      const item = typeof d.item === "string" ? cosmetic(d.item) : undefined;
      return item?.label ?? text(d.title) ?? "Wardrobe";
    }
    default:
      return "Gold";
  }
}

/**
 * The second, muted word on a line, or null: what kind of movement it was when the label alone
 * does not say ("gold rush", "reopened", "luxury", "refund", who bought your reward, "credit given to
 * Hue", "credit from Omie"). Short, so the line stays one line.
 */
export function purseNote(row: PurseLabelInput): string | null {
  const d = row.detail ?? {};
  switch (row.kind) {
    case "bounty": {
      // Give credit (docs/credit.md): the gold came with someone else's credit, whatever paid it.
      const from = text(d.credited_from);
      if (from) return creditFromNote(from);
      // Shared credit (docs/shares.md): a helper's part names who they did it with; the finisher's, who helped.
      if (d.share === true) return sharedNote(names(d.with));
      if (Array.isArray(d.shared_with) && d.shared_with.length > 0) return sharedNote(names(d.shared_with));
      return d.flash === true ? "gold rush" : null;
    }
    case "reversal":
      if (d.reason === "credit") return creditGivenNote(text(d.to));
      if (d.reason === "share") return "split again";
      return d.reason === "delete" ? "deleted" : "reopened";
    case "reward_income": {
      const buyer = text(d.buyer);
      return buyer ? `${buyer} bought it` : "sold";
    }
    case "luxury":
      return "luxury";
    case "refund":
      return "refund";
    case "bonus":
      return typeof d.trinket === "string" ? "trinket" : "badge";
    case "wager":
      return typeof d.pot === "number" ? "won" : "stake";
    case "roulette":
      return "someone else did it";
    default:
      return null;
  }
}

/** The amount as the purse writes it: "30", "1,000" earned, "-400" spent. No plus sign. */
export function purseAmountLabel(amount: number): string {
  const n = Math.round(amount);
  const abs = Math.abs(n).toLocaleString("en-US");
  return n < 0 ? `-${abs}` : abs;
}

/** Whether a row took gold away (drawn muted rather than in gold). */
export function isPurseSpend(amount: number): boolean {
  return amount < 0;
}

/** The `before` cursor that follows a row: its time and id, so rows sharing a time page cleanly. */
export function purseCursor(row: Pick<PurseRow, "created_at" | "id">): string {
  return `${row.created_at}|${row.id}`;
}

/** Splits a `before` cursor back into its time and (optional) id; null for an unusable one. */
export function parsePurseCursor(raw: string | null | undefined): { at: string; id: string | null } | null {
  if (!raw) return null;
  const bar = raw.indexOf("|");
  const at = (bar < 0 ? raw : raw.slice(0, bar)).trim();
  const id = bar < 0 ? null : raw.slice(bar + 1).trim() || null;
  if (!at || at.length > 40 || (id !== null && id.length > 64)) return null;
  return { at, id };
}
